// The roster's close-stage cell.
//
// Stage is NOT a stored field — the server derives it per request from five independent signals
// (`firm_close_stage` in admin.rs): connection health, share views, active share links, an issued
// revision, and whether the newest review checkpoint matches the data currently displayed. So there
// is nothing a dropdown could set; a written value would vanish on the next fetch.
//
// Exactly one transition has a real, single-write path: needs_review -> reviewed, by posting a
// review checkpoint against the signature the roster already carries. That is what this offers.
// Every other stage is presented read-only with a plain-language note about what drives it, and the
// two that are reachable through the product (issued, shared) link to where that work happens.

import React, { useEffect, useRef, useState } from 'react'
import type { ServerFirmPortfolioClient } from '../../../data/ServerApiClient'

export const STAGE_LABELS: Record<string, string> = {
  needs_review: 'Needs review',
  reviewed: 'Reviewed',
  issued: 'Issued',
  shared: 'Shared',
  viewed: 'Viewed by client',
  stale: 'Stale · reconnect',
}

/** What actually moves a client into this stage, in the firm's own terms. */
export const STAGE_DRIVER: Record<string, string> = {
  needs_review: 'No review recorded against the current numbers yet.',
  reviewed: 'Someone reviewed this against exactly the data showing now. New data reopens it.',
  issued: 'A report revision has been issued from Presentations.',
  shared: 'A live share link is active for the issued revision.',
  viewed: 'The client has opened the shared report — this cannot be set by hand.',
  stale: 'The accounting connection needs reconnecting; this outranks every other stage.',
}

export type CockpitStageCellProps = {
  client: ServerFirmPortfolioClient
  busy?: boolean
  onMarkReviewed: () => void
  onOpenPresentations: () => void
}

/** Whether the one supported transition is available for this client right now. */
export function canMarkReviewed(client: ServerFirmPortfolioClient): boolean {
  return (
    client.close_stage === 'needs_review' &&
    !!client.live_board_report?.source_signature
  )
}

export function CockpitStageCell({
  client,
  busy,
  onMarkReviewed,
  onOpenPresentations,
}: CockpitStageCellProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const stage = client.close_stage
  const actionable = canMarkReviewed(client)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const label = STAGE_LABELS[stage] || stage
  const driver = STAGE_DRIVER[stage] || ''

  // Stages with nothing to offer stay a plain chip — a menu that only ever says "you can't do this
  // here" is worse than no menu.
  if (!actionable && stage !== 'issued' && stage !== 'shared') {
    return (
      <span className={`stage-chip st-${stage}`} title={driver}>
        {label}
      </span>
    )
  }

  return (
    <div className="stage-cell" ref={ref}>
      <button
        type="button"
        className={`stage-chip st-${stage} interactive`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={driver}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        {busy ? 'Saving…' : label}
        <span className="stage-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="stage-menu" role="menu">
          <div className="stage-menu-note">{driver}</div>
          {actionable && (
            <button
              type="button"
              role="menuitem"
              className="stage-menu-item"
              onClick={() => {
                setOpen(false)
                onMarkReviewed()
              }}
            >
              Mark reviewed
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="stage-menu-item subtle"
            onClick={() => {
              setOpen(false)
              onOpenPresentations()
            }}
          >
            {stage === 'issued' || stage === 'shared'
              ? 'Manage in Presentations →'
              : 'Issue or share in Presentations →'}
          </button>
        </div>
      )}
    </div>
  )
}

export default CockpitStageCell
